import { useRef, useState, useEffect } from "react";

// IntersectionObserver + CSS transitions — same API as before, but no animation
// library on the critical path (keeps the main JS bundle small and fast).
const useInViewOnce = (rootMargin = "-10% 0px -10% 0px") => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return undefined;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { rootMargin }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [inView, rootMargin]);
  return [ref, inView];
};

// Staggered fade-up on scroll into view.
export const Reveal = ({ children, delay = 0, y = 40, className = "" }) => {
  const [ref, inView] = useInViewOnce();
  return (
    <div
      ref={ref}
      className={`reveal ${inView ? "revealed" : ""} ${className}`}
      style={{ "--reveal-y": `${y}px`, transitionDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
};

// Masked line-by-line reveal for large headings.
export const MaskText = ({ lines = [], className = "", delay = 0, animate = false, as: Tag = "h1" }) => {
  const [ref, inView] = useInViewOnce();
  const show = animate || inView;
  return (
    <Tag ref={ref} className={className}>
      {lines.map((line, i) => (
        <span key={i} className="mask-line">
          <span
            className={`mask-inner ${show ? "mask-shown" : ""}`}
            style={{ transitionDelay: `${delay + i * 0.12}s` }}
          >
            {line}
          </span>
        </span>
      ))}
    </Tag>
  );
};

export const Line = ({ className = "", delay = 0 }) => {
  const [ref, inView] = useInViewOnce("0px");
  return (
    <div
      ref={ref}
      className={`h-px bg-ink/15 origin-left line-anim ${inView ? "line-shown" : ""} ${className}`}
      style={{ transitionDelay: `${delay}s` }}
    />
  );
};
